<template>
	<div>
		<TransitionGroup v-if="hasTopics" name="tag" tag="div" class="tag-list">
			<template v-for="item in displayItems" :key="item.key">
				<group-card
					v-if="item.kind === 'group'"
					:name="item.group.name"
					:icon_url="item.group.icon_url"
					:expanded="isExpanded(item.group.id)"
					:sort="item.order"
					@click="toggleGroup(item.group.id)"
				/>
				<tag-card
					v-else
					:name="item.topic.name"
					:icon_url="item.topic.icon_url"
					:sort="item.order"
					:variant="item.kind === 'child' ? 'child' : 'default'"
					:pro="shouldMarkPro(item.topic)"
					@click="onTopicClick(item.topic)"
				/>
			</template>
		</TransitionGroup>
		<loader-component v-else />
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useCommonStore } from '@/stores/common'
import { useThreadStore } from '@/stores/thread'
import { useComposerStore } from '@/stores/composer'
import { useAuthRedirect } from '@/composables/useAuthRedirect'
import { useFeatureGate } from '@/composables/useFeatureGate'
import type { CreateThreadData, TopicObject, TopicGroupObject, TopicType } from '@/types'
import TagCard from '@/components/ui/TagCard.vue'
import GroupCard from '@/components/ui/GroupCard.vue'
import LoaderComponent from '@/components/LoaderComponent.vue'
const { ensureAuthenticated } = useAuthRedirect()
const { checkTopic, shouldMarkPro, runGate } = useFeatureGate()

const commonStore = useCommonStore()
const router = useRouter()
const threadStore = useThreadStore()
const composerStore = useComposerStore()
const hasTopics = computed(() => commonStore.hasTopics)
const topicsArray = computed(() => commonStore.topicsArray)

// Раскрытые группы (по id)
const expandedGroups = ref<Set<number>>(new Set())
const isExpanded = (id: number) => expandedGroups.value.has(id)
function toggleGroup(id: number) {
	const next = new Set(expandedGroups.value)
	next.has(id) ? next.delete(id) : next.add(id)
	expandedGroups.value = next
}

// Плоский список для рендера в одном flex-контейнере:
// после раскрытой группы сразу идут её дочерние темы.
// order — сквозной индекс для CSS `order`, чтобы дочерние темы шли сразу
// за своей группой, а верхний уровень сохранял порядок по полю sort.
type DisplayItem =
	| { kind: 'topic'; key: string; order: number; topic: TopicObject }
	| { kind: 'group'; key: string; order: number; group: TopicGroupObject }
	| { kind: 'child'; key: string; order: number; topic: TopicObject }

const displayItems = computed<DisplayItem[]>(() => {
	const result: DisplayItem[] = []
	const topLevel = [...topicsArray.value].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
	let order = 0
	for (const item of topLevel) {
		if (item.element_type === 'group') {
			const group = item as TopicGroupObject
			result.push({ kind: 'group', key: `g-${group.id}`, order: order++, group })
			if (isExpanded(group.id)) {
				const children = [...group.topics].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
				for (const topic of children) {
					result.push({ kind: 'child', key: `g-${group.id}-t-${topic.id}`, order: order++, topic })
				}
			}
		} else {
			const topic = item as TopicObject
			result.push({ kind: 'topic', key: `t-${topic.id}`, order: order++, topic })
		}
	}
	return result
})

onMounted(async () => {
	if (!hasTopics.value || topicsArray.value.length === 0) {
		try {
			await commonStore.fetchTopics()
		} catch (error) {
			console.error('Failed to fetch topics:', error)
		}
	}
})

async function handleCardClick(data: CreateThreadData) {
	await ensureAuthenticated()
	await threadStore.clearProccess()
	threadStore.clearSecretMode()
	composerStore.clearReasoningMode()
	if (data.type !== 'image') {
		commonStore.enableSendingMessage()
	}
	if (data.name) threadStore.setTopicName(data.name)
	// Выставляем тип топика для ЛЮБОГО типа (в духе авторитетной доустановки в ThreadView.onMounted):
	// иначе, например, для 'image' не поднимался imgMode → не показывались тайтл «Генерация
	// изображения» и блок примеров до обновления страницы.
	if (data.type) threadStore.setTopicType(data.type as TopicType)
	threadStore.createThread(data)
	router.push(`/thread`)
}

function onTopicClick(topic: TopicObject) {
	// Гейт — до всего остального (в т.ч. до clearProccess), чтобы отказ не ломал текущий тред.
	if (!runGate(checkTopic(topic), { kind: 'topic', topicName: topic.name })) return
	handleCardClick({
		topic_id: topic.id,
		name: topic.name,
		type: topic.type,
		model_name: topic.model
	})
}
</script>

<style lang="scss" scoped>
.tag-list {
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 8px;
}

// Плавное появление/исчезновение дочерних тем
.tag-enter-active,
.tag-leave-active {
	transition:
		opacity 0.3s ease,
		transform 0.3s ease;
}
.tag-enter-from,
.tag-leave-to {
	opacity: 0;
	transform: translateY(-6px) scale(0.95);
}
// Убираем из потока при закрытии, чтобы соседние плитки плавно перестроились
.tag-leave-active {
	position: absolute;
}
// Плавное смещение остальных плиток
.tag-move {
	transition: transform 0.3s ease;
}
</style>
